/**
 * The one width at which web stops being a desktop page and starts being a
 * phone.
 *
 * React Native Web builds styles as JS objects, not stylesheets, so a CSS
 * `@media` rule cannot reach them. The equivalent is reading the width with
 * `useWindowDimensions` — which re-renders on resize, unlike a one-time
 * `Dimensions.get` — and branching on it. This module exists so that branch is
 * ONE number in ONE place: the screens that care are already several, and a
 * threshold written inline at each of them is the shape this repository keeps
 * finding half-updated.
 *
 * ## What "compact" is allowed to change, and what it must not
 *
 * Compact means "web, on a screen the size of a phone, should look like the
 * phone app". It is additive by construction: every rule that reads this flag
 * keeps the native branch and the wide-web branch exactly as they were, and
 * only routes narrow web to the native one. Nothing that already worked at
 * desktop width, and nothing on iOS or Android, may change because of it —
 * `Platform.OS` still decides the shape, this only decides which shape narrow
 * web borrows.
 *
 * Inclusive at the boundary: 500 is compact, 501 is not. Stated because the
 * off-by-one is invisible in a screenshot and shows up as a header that
 * disagrees with a button at exactly one width.
 */
export const COMPACT_MAX_WIDTH = 500;

/**
 * Whether a viewport this wide should wear the phone layout.
 *
 * Takes the width rather than calling `useWindowDimensions` itself, so it stays
 * a plain function the test suite can import — `logo.tsx` and every screen
 * carry JSX, which Node's test runner strips types from but cannot parse.
 */
export function isCompactWidth(width: number): boolean {
  return width <= COMPACT_MAX_WIDTH;
}
